import axios, { AxiosError } from 'axios';
import { FC, useEffect, useState } from 'react';
import { BrowserRouter, Route, Switch, useLocation } from 'react-router-dom';
import iconLoading from './assets/images/icon_loading_circle.gif';
import { CUSTOMER_API_URL } from './constants';
import { masterDataContext } from './context';
import { useMasterData } from './hooks/use-master-data';
import { CancellationCushion } from './pages/CancellationCushion';
import { SubscriptionDetail } from './pages/SubscriptionDetail';
import { SubscriptionList } from './pages/SubscriptionList';
import { ParentParams, Setting } from './types/constant/PagesPropsType';
import './assets/css/loading.css';

const History: FC<{ children: any }> = ({ children }) => {
  const pathname = useLocation().pathname;

  useEffect(() => {
    // 画面遷移時のURL変更で使用
    window.parent.postMessage({ action: 'pushHistory', path: pathname }, '*');
  }, [pathname]);

  return children;
};

export const App = () => {
  const master = useMasterData();
  const [parentParams, setParentParams] = useState<ParentParams>({
    customerTags: [],
    totalSpent: '',
  });
  const [config, setConfig] = useState<{
    token: string;
    styleTexts: string[];
    styleUrls: string[];
  }>();

  const resize = (height: number) => {
    // リサイズで使用
    window.parent.postMessage({ action: 'resetWindow', height }, '*');
  };

  // Ref: https://github.com/mdn/translated-content/blob/main/files/ja/web/api/web_storage_api/using_the_web_storage_api/index.html#L54
  const storageAvailable = () => {
    var storage;
    try {
      storage = window.localStorage;
      var x = '__storage_test__';
      storage.setItem(x, x);
      storage.removeItem(x);
      return true;
    } catch (e) {
      return (
        e instanceof DOMException &&
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === 'QuotaExceededError' ||
          // Firefox
          e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        storage &&
        storage.length !== 0
      );
    }
  };

  const loggedIn = (shopId: string, customerId: string) => {
    const shopTokenStr = localStorage.getItem(shopId);
    if (!shopTokenStr) return false;

    const shopToken = JSON.parse(shopTokenStr);

    return (
      shopToken.jwt &&
      shopToken.customerId === customerId &&
      shopToken.jwtExpiredTimestamp &&
      Number(shopToken.jwtExpiredTimestamp) >= new Date().getTime()
    );
  };

  const initializeEvent = (event: MessageEvent) => {
    if (event.data.customerTags) {
      const next = event.data;
      setParentParams((prev) => {
        const same =
          prev.totalSpent === next.totalSpent &&
          prev.npFlg === next.npFlg &&
          prev.customerTags.length === next.customerTags.length &&
          prev.customerTags.every((v, i) => v === next.customerTags[i]);

        return same ? prev : next;
      });

      return;
    }
    // We can't rejected by event.origin because Shopify Store domain is not unique.
    // So, We check data structure only.
    const { code, styleTexts, styleUrls, shopId, customerId } = event.data;
    if (code === undefined || !styleTexts || !styleUrls || !shopId) {
      return;
    }

    if (storageAvailable() && loggedIn(shopId, customerId)) {
      const shopTokenStr = localStorage.getItem(shopId);
      const shopToken = JSON.parse(shopTokenStr!);

      setConfig({ token: shopToken.jwt, styleTexts, styleUrls });

      return;
    }

    axios
      .post(`${CUSTOMER_API_URL}?action=token`, {
        code,
      })
      .then((response) => {
        window.removeEventListener('message', initializeEvent);

        const token = response.data;
        const jwtExpiredTimestamp = new Date().getTime() + 60 * 60 * 1000; // 1 hour

        if (storageAvailable()) {
          const shopToken = {
            jwt: token,
            customerId,
            jwtExpiredTimestamp: jwtExpiredTimestamp,
          };
          localStorage.setItem(shopId, JSON.stringify(shopToken));
          console.log('stored localStorage');
        }

        setConfig({ token, styleTexts, styleUrls });
      })
      .catch((error: AxiosError) => {
        if (!error.response) {
          // Debug for now
          throw new Error(`Network Error but not response: ${error.message}`);
        }

        if (error.response.status >= 500) {
          throw error;
        }
      });
  };

  useEffect(() => {
    window.addEventListener('message', initializeEvent);

    window.addEventListener('resize', () => {
      // Safari doesn't compute "float: left" height.
      if (!document.querySelector('body')!.scrollHeight) {
        return;
      }

      const height = document.querySelector('body')!.scrollHeight; // 2023.11 修正のためbuffer分削除
      // const height = document.querySelector('body')!.scrollHeight + 100; // buffer;

      resize(height);
    });

    window.parent.postMessage({ action: 'initialize' }, '*');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!config) {
    return (
      <div className="loadingContainer">
        <div className="loadingContent">
          <div className="loading">
            <img
              src={iconLoading}
              alt=""
              width={64}
              height={64}
            />
            <div>読み込み中です。しばらくお待ちください。</div>
          </div>
        </div>
      </div>
    );
  }

  const setting: Setting = {
    token: config.token,
    resize,
  };

  config.styleTexts.forEach((styleText) => {
    const style = document.createElement('style');
    style.innerText = styleText;

    document.body.appendChild(style);
  });

  config.styleUrls.forEach((url) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;

    document.body.appendChild(link);
  });

  return (
    <masterDataContext.Provider value={master}>
      <BrowserRouter basename="/?path=">
        <History>
          <Switch>
            <Route path="/subscriptions/:id">
              <SubscriptionDetail
                setting={setting}
                parentParams={parentParams}
              />
            </Route>
            <Route path="/cancel/:id">
              <CancellationCushion
                setting={setting}
                parentParams={parentParams}
              />
            </Route>
            <Route path="/">
              <SubscriptionList
                setting={setting}
                parentParams={parentParams}
              />
            </Route>
          </Switch>
        </History>
      </BrowserRouter>
    </masterDataContext.Provider>
  );
};
